import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leadApi, userApi, appointmentApi } from '../lib/api';
import api from '../lib/api';
import LeadPipelineView from '../components/leads/LeadPipelineView';
import { leadStatusLabels } from '../lib/utils';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Loader2, AlertTriangle } from 'lucide-react';

// Drag & Drop imports
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';

// Import extracted components
import {
  LeadStatsCards,
  LeadSearchFilter,
  LeadKanbanView,
  LeadListView,
  NewLeadModal,
  LeadCard,
  LeadDetailModal,
  AssignTechnicianModal,
  TransitionConfirmModal,
  DraggableLeadCard,
} from '../components/leads';

// Import dialogs per move
import { AppointmentDialog, LostReasonDialog, ConvertConfirmDialog } from '../components/leads/LeadMoveDialogs';
import LostLeadsPanel from '../components/leads/LostLeadsPanel';

// Workflow configuration
const workflowSteps = [
  { status: 'nuovo', label: 'Nuovo' },
  { status: 'contattato', label: 'Contattato' },
  { status: 'assegnato', label: 'Assegnato' },
  { status: 'appuntamento_fissato', label: 'Appuntamento' },
  { status: 'sopralluogo_effettuato', label: 'Sopralluogo' },
  { status: 'in_preventivazione', label: 'Preventivazione' },
  { status: 'preventivo_presentato', label: 'Presentato' },
  { status: 'in_trattativa', label: 'Trattativa' },
  { status: 'in_contratto', label: 'Contratto' },
  { status: 'convertito', label: 'Convertito' },
];

const LeadsPage = () => {
  const { isAdmin, isProjectManager, user, hasPermission, canCreateAppointmentForLead } = useAuth();
  const navigate = useNavigate();
  // Fase F1 · rileva flag dual-pipeline via pipeline-counts (404 = flag OFF → UI legacy)
  const [pipelineEnabled, setPipelineEnabled] = useState(null);
  useEffect(() => {
    let active = true;
    api.get('/leads/pipeline-counts')
      .then(() => active && setPipelineEnabled(true))
      .catch(() => active && setPipelineEnabled(false));
    return () => { active = false; };
  }, []);
  const [leads, setLeads] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('kanban');
  
  // Modals
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showLeadDetail, setShowLeadDetail] = useState(null); // Mantenuto per compatibilità, non più usato per click card
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(null);
  const [transitionNote, setTransitionNote] = useState('');
  const [selectedTechnician, setSelectedTechnician] = useState('');
  const [transitioning, setTransitioning] = useState(false);

  // Navigazione al dettaglio lead (sostituisce apertura modal)
  const handleNavigateToLead = useCallback((lead) => {
    navigate(`/leads/${lead.id}`);
  }, [navigate]);
  
  // Contract and Estimates for lead detail
  const [leadContract, setLeadContract] = useState(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [leadEstimates, setLeadEstimates] = useState([]);
  const [loadingEstimates, setLoadingEstimates] = useState(false);
  const [estimatesCountMap, setEstimatesCountMap] = useState({});
  const [estimatesInProgressMap, setEstimatesInProgressMap] = useState({});
  
  // Delete lead state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  
  // Edit mode for lead detail
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editLeadData, setEditLeadData] = useState(null);
  const [savingLead, setSavingLead] = useState(false);
  
  // Anti-doppio-click: flag per prevenire creazione duplicati
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  
  // Move menu dialogs
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(null);
  const [showLostDialog, setShowLostDialog] = useState(null);
  const [showConvertDialog, setShowConvertDialog] = useState(null);
  const [movingLeadId, setMovingLeadId] = useState(null);
  const [rooms, setRooms] = useState([]);
  
  // Utenti CRM (per partecipanti appuntamenti)
  const [allUsers, setAllUsers] = useState([]);
  
  // Google Calendar status
  const [googleConnected, setGoogleConnected] = useState(false);
  
  // Drag & Drop state
  const [activeDragLead, setActiveDragLead] = useState(null);
  const [isDragEnabled, setIsDragEnabled] = useState(false);
  
  // Check viewport width for drag & drop (>1024px = enabled)
  useEffect(() => {
    const checkViewport = () => {
      setIsDragEnabled(window.innerWidth > 1024);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);
  
  // Drag & Drop sensors - solo pointer (mouse/touch)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Richiede 8px di movimento prima di iniziare il drag
      },
    })
  );
  
  const [newLead, setNewLead] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    is_business: false,
    business_name: '',
    fiscal_code: '',
    // Nuovi campi indirizzo cliente
    customer_address: '',
    customer_city: '',
    customer_cap: '',
    customer_province: '',
    // Nuovi campi indirizzo cantiere
    site_same_as_customer_address: true, // Default: coincide
    site_address: '',
    site_city: '',
    site_cap: '',
    site_province: '',
    site_notes: '',
    // Legacy (per retrocompatibilità, sincronizzato con customer_*)
    property_address: '',
    property_city: '',
    property_cap: '',
    source: '',
    notes: ''
  });

  // Unsaved changes tracking
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Check if newLead form has any data
  const hasUnsavedChanges = useCallback(() => {
    return showNewLeadModal && (
      newLead.first_name.trim() || 
      newLead.last_name.trim() || 
      newLead.email.trim() || 
      newLead.phone.trim() || 
      newLead.customer_address?.trim() ||
      newLead.customer_city?.trim() ||
      newLead.site_address?.trim() ||
      newLead.notes.trim()
    );
  }, [showNewLeadModal, newLead]);

  // Warn before browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = 'Hai modifiche non salvate. Sei sicuro di voler uscire?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle closing modal with unsaved changes
  const handleCloseNewLeadModal = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedDialog(true);
    } else {
      setShowNewLeadModal(false);
    }
  };

  const handleConfirmDiscard = () => {
    setNewLead({
      first_name: '', last_name: '', email: '', phone: '', 
      is_business: false, business_name: '', fiscal_code: '',
      customer_address: '', customer_city: '', customer_cap: '', customer_province: '',
      site_same_as_customer_address: true, site_address: '', site_city: '', site_cap: '', site_province: '', site_notes: '',
      property_address: '', property_city: '', property_cap: '',
      source: '', notes: ''
    });
    setShowNewLeadModal(false);
    setShowUnsavedDialog(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // Load contract when lead detail is opened
  useEffect(() => {
    if (showLeadDetail && ['in_contratto', 'convertito'].includes(showLeadDetail.status)) {
      loadLeadContract(showLeadDetail.id);
    } else {
      setLeadContract(null);
    }
    
    if (showLeadDetail) {
      loadLeadEstimates(showLeadDetail.id);
    } else {
      setLeadEstimates([]);
    }
  }, [showLeadDetail]);

  // Load estimates count for all leads
  useEffect(() => {
    if (leads.length > 0) {
      loadEstimatesCountForLeads();
    }
  }, [leads]);

  // Status preventivi considerati "in lavorazione"
  const IN_PROGRESS_ESTIMATE_STATUSES = [
    "in_bozza",
    "in_compilazione",
    "appuntamento_in_sede_pianificato",
    "in_attesa_validazione_admin"
  ];

  const loadEstimatesCountForLeads = async () => {
    try {
      const response = await fetch(`/api/estimates`, { withCredentials: true });
      if (response.ok) {
        const allEstimates = await response.json();
        const countMap = {};
        const inProgressMap = {};
        
        allEstimates.forEach(est => {
          if (est.lead_id && !est.is_deleted) {
            // Conteggio totale preventivi per lead
            countMap[est.lead_id] = (countMap[est.lead_id] || 0) + 1;
            
            // Mappa lead con almeno un preventivo in lavorazione
            if (IN_PROGRESS_ESTIMATE_STATUSES.includes(est.status)) {
              inProgressMap[est.lead_id] = true;
            }
          }
        });
        
        setEstimatesCountMap(countMap);
        setEstimatesInProgressMap(inProgressMap);
      }
    } catch (error) {
      console.error('Error loading estimates count:', error);
    }
  };

  const loadLeadEstimates = async (leadId) => {
    setLoadingEstimates(true);
    try {
      const response = await fetch(`/api/estimates?lead_id=${leadId}`, { withCredentials: true });
      if (response.ok) {
        const estimates = await response.json();
        setLeadEstimates(estimates);
      }
    } catch (error) {
      console.error('Error loading estimates:', error);
    } finally {
      setLoadingEstimates(false);
    }
  };

  const loadLeadContract = async (leadId) => {
    setLoadingContract(true);
    try {
      const response = await fetch(`/api/leads/${leadId}/contract`, { withCredentials: true });
      if (response.ok) {
        const contract = await response.json();
        setLeadContract(contract);
      }
    } catch (error) {
      console.error('Error loading contract:', error);
    } finally {
      setLoadingContract(false);
    }
  };

  const loadData = async () => {
    try {
      const [leadsRes, techRes] = await Promise.all([
        leadApi.getAll(),
        userApi.getTechnicians()
      ]);
      setLeads(leadsRes.data);
      setTechnicians(techRes.data);
      
      // Carica utenti CRM (per partecipanti appuntamenti)
      try {
        const usersRes = await fetch('/api/users', { credentials: 'include' });
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          // Filtra solo utenti attivi
          setAllUsers(usersData.filter(u => u.is_active !== false));
        }
      } catch (userError) {
        console.error('Error loading users:', userError);
      }
      
      // Verifica stato Google Calendar
      try {
        const googleRes = await fetch('/api/auth/google-calendar/status', { credentials: 'include' });
        if (googleRes.ok) {
          const googleData = await googleRes.json();
          setGoogleConnected(googleData.connected || false);
        }
      } catch (googleError) {
        console.error('Error checking Google status:', googleError);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dei lead');
    } finally {
      setLoading(false);
    }
  };

  // Start editing lead
  const startEditingLead = () => {
    if (showLeadDetail) {
      setEditLeadData({
        first_name: showLeadDetail.first_name || '',
        last_name: showLeadDetail.last_name || '',
        email: showLeadDetail.email || '',
        phone: showLeadDetail.phone || '',
        is_business: showLeadDetail.is_business || false,
        business_name: showLeadDetail.business_name || '',
        fiscal_code: showLeadDetail.fiscal_code || '',
        property_address: showLeadDetail.property_address || '',
        property_city: showLeadDetail.property_city || '',
        property_cap: showLeadDetail.property_cap || '',
        source: showLeadDetail.source || ''
      });
      setIsEditingLead(true);
    }
  };

  const cancelEditingLead = () => {
    setIsEditingLead(false);
    setEditLeadData(null);
  };

  const saveLeadChanges = async () => {
    if (!showLeadDetail || !editLeadData) return;
    
    setSavingLead(true);
    try {
      const response = await fetch(
        `/api/leads/${showLeadDetail.id}`,
        {
          method: 'PUT',
          headers: { withCredentials: true, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...editLeadData, email: editLeadData.email || null })
        }
      );

      if (response.ok) {
        const updatedLead = await response.json();
        toast.success('Lead aggiornato con successo');
        setShowLeadDetail(updatedLead);
        setIsEditingLead(false);
        setEditLeadData(null);
        await loadData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio del lead');
    } finally {
      setSavingLead(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    
    // Prevenzione doppio-click: blocca se già in corso
    if (isCreatingLead) {
      return;
    }
    
    setIsCreatingLead(true);
    try {
      // Prepara i dati sincronizzando i campi legacy con i nuovi
      const leadData = { 
        ...newLead, 
        email: newLead.email || null,
        // Sincronizza property_address con customer_address per retrocompatibilità
        property_address: newLead.customer_address || newLead.property_address || '',
        property_city: newLead.customer_city || newLead.property_city || '',
        property_cap: newLead.customer_cap || newLead.property_cap || '',
      };
      await leadApi.create(leadData);
      toast.success('Lead creato con successo');
      setNewLead({
        first_name: '', last_name: '', email: '', phone: '', 
        is_business: false, business_name: '', fiscal_code: '',
        customer_address: '', customer_city: '', customer_cap: '', customer_province: '',
        site_same_as_customer_address: true, site_address: '', site_city: '', site_cap: '', site_province: '', site_notes: '',
        property_address: '', property_city: '', property_cap: '',
        source: '', notes: ''
      });
      setShowNewLeadModal(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione del lead');
    } finally {
      setIsCreatingLead(false);
    }
  };

  const handleTransition = async (leadId, newStatus, technicianId = null) => {
    setTransitioning(true);
    try {
      await leadApi.transition(leadId, {
        new_status: newStatus,
        notes: transitionNote,
        assigned_technician_id: technicianId
      });

      toast.success(`Lead aggiornato a "${leadStatusLabels[newStatus] || newStatus}"`);
      setShowTransitionModal(null);
      setShowAssignModal(false);
      setTransitionNote('');
      setSelectedTechnician('');
      setShowLeadDetail(null);
      
      if (newStatus === 'in_preventivazione') {
        await openOrCreateEstimate(leadId);
      } else {
        await loadData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Errore nella transizione');
    } finally {
      setTransitioning(false);
    }
  };

  // Delete lead function (admin only)
  const handleDeleteLead = async () => {
    if (!showLeadDetail) return;
    
    setDeletingLead(true);
    try {
      await leadApi.delete(showLeadDetail.id);
      toast.success(`Lead "${showLeadDetail.first_name} ${showLeadDetail.last_name}" eliminato`);
      setShowDeleteDialog(false);
      setShowLeadDetail(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del lead');
    } finally {
      setDeletingLead(false);
    }
  };

  // ==================== MOVE MENU HANDLER ====================
  const handleMove = async (lead, targetStatus) => {
    // Stati che richiedono modali
    const needsAppointment = ['appuntamento_fissato'].includes(targetStatus.value);
    const needsTechnician = ['assegnato'].includes(targetStatus.value) && !lead.assigned_technician_id;
    const needsReason = ['perso'].includes(targetStatus.value);
    const needsValidation = ['convertito'].includes(targetStatus.value);
    
    // Apri modale se serve
    if (needsAppointment) {
      setShowAppointmentDialog(lead);
      return;
    }
    
    if (needsTechnician) {
      // Apri modale assegnazione
      setShowLeadDetail(lead);
      setShowAssignModal(true);
      return;
    }
    
    if (needsReason) {
      setShowLostDialog(lead);
      return;
    }
    
    if (needsValidation) {
      // Verifica se ha preventivo o contratto
      const hasEstimate = estimatesCountMap[lead.id] > 0;
      setShowConvertDialog({ ...lead, hasEstimate });
      return;
    }
    
    // Transizione diretta senza modale
    await executeMove(lead.id, targetStatus.value, {});
  };

  const executeMove = async (leadId, toStatus, extraData = {}) => {
    setMovingLeadId(leadId);
    try {
      const payload = { to_status: toStatus, ...extraData };
      await leadApi.transition(leadId, payload);
      
      toast.success(`Lead spostato a "${leadStatusLabels[toStatus] || toStatus}"`);
      
      // Chiudi tutti i dialogs
      setShowAppointmentDialog(null);
      setShowLostDialog(null);
      setShowConvertDialog(null);
      setShowAssignModal(false);
      setShowLeadDetail(null);
      
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nello spostamento');
    } finally {
      setMovingLeadId(null);
    }
  };

  const handleAppointmentConfirm = async (leadId, toStatus, formData) => {
    setMovingLeadId(leadId);
    let appointmentCreated = false;
    
    try {
      const lead = leads.find(l => l.id === leadId);
      
      // Aggiorna tecnico SOLO se è cambiato (evita 403 per tecnici)
      // Admin e PM possono cambiare tecnico assegnato
      const technicianChanged = formData.technician_id && 
                                formData.technician_id !== lead?.assigned_technician_id;
      
      if (technicianChanged && (isAdmin || isProjectManager)) {
        try {
          await leadApi.update(leadId, { assigned_technician_id: formData.technician_id });
        } catch (updateError) {
          console.warn('Could not update technician:', updateError);
          // Non bloccare - continua con la creazione appuntamento
        }
      }
      
      // Crea l'appuntamento
      const appointmentData = {
        lead_id: leadId,
        title: `Appuntamento - ${lead?.first_name} ${lead?.last_name}`,
        appointment_date: formData.appointment_date,
        appointment_time: formData.appointment_time,
        room_id: formData.room_id || null,
        notes: formData.notes || '',
        assigned_technician_id: formData.technician_id || lead?.assigned_technician_id,
        status: 'scheduled',
        send_confirmation_email: formData.send_confirmation_email ?? true
      };
      
      await appointmentApi.create(appointmentData);
      appointmentCreated = true;
      
      // Transizione lead (operazione secondaria - non bloccare se fallisce)
      try {
        await leadApi.transition(leadId, { to_status: toStatus });
      } catch (transitionError) {
        console.warn('Could not transition lead:', transitionError);
        toast.warning('Appuntamento creato, ma lo stato del lead non è stato aggiornato');
      }
      
      toast.success('Appuntamento fissato con successo');
      setShowAppointmentDialog(null);
      await loadData();
    } catch (error) {
      if (appointmentCreated) {
        // L'appuntamento è stato creato ma qualcos'altro è fallito
        toast.warning('Appuntamento creato, ma si è verificato un errore secondario');
        setShowAppointmentDialog(null);
        await loadData();
      } else {
        toast.error(error.response?.data?.detail || 'Errore nel fissare l\'appuntamento');
      }
    } finally {
      setMovingLeadId(null);
    }
  };

  const handleLostConfirm = async (leadId, toStatus, formData) => {
    setMovingLeadId(leadId);
    try {
      // Il backend richiede motivo_perdita (codice) come campo esplicito
      await leadApi.transition(leadId, {
        to_status: toStatus,
        notes: formData.notes || null,
        motivo_perdita: formData.reason,
        motivo_perdita_note: formData.notes || null,
      });
      
      toast.success('Lead segnato come perso');
      setShowLostDialog(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setMovingLeadId(null);
    }
  };

  const handleConvertConfirm = async (leadId, toStatus, formData) => {
    await executeMove(leadId, toStatus, formData);
  };
  // ==================== END MOVE MENU HANDLER ====================

  // ==================== DRAG & DROP HANDLERS ====================
  const handleDragStart = (event) => {
    const { active } = event;
    const leadId = active.id;
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setActiveDragLead(lead);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragLead(null);
    
    // Se non c'è un target o il drop è invalido
    if (!over) return;
    
    const leadId = active.id;
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;
    
    // Estrai lo status dalla colonna target
    // L'ID della colonna è "column-{status}"
    const overId = over.id;
    let targetStatus = null;
    
    if (typeof overId === 'string' && overId.startsWith('column-')) {
      targetStatus = overId.replace('column-', '');
    } else {
      // Potrebbe essere il drop su una card, trova la colonna parent
      const overData = over.data?.current;
      if (overData?.status) {
        targetStatus = overData.status;
      }
    }
    
    // Se non troviamo un target status o è lo stesso, ignora
    if (!targetStatus || lead.status === targetStatus) return;
    
    // Non permettere drag verso 'non_risponde' (stato interno)
    if (targetStatus === 'non_risponde') return;
    
    // Usa la stessa logica del menu "Sposta a..."
    const targetStatusObj = { 
      value: targetStatus, 
      label: leadStatusLabels[targetStatus] || targetStatus 
    };
    
    // handleMove gestisce tutto: modali, transizioni dirette, etc.
    handleMove(lead, targetStatusObj);
  };

  const handleDragCancel = () => {
    setActiveDragLead(null);
  };
  // ==================== END DRAG & DROP HANDLERS ====================

  const openOrCreateEstimate = async (leadId) => {
    try {
      const checkResponse = await fetch(`/api/estimates?lead_id=${leadId}`, { withCredentials: true });
      
      if (checkResponse.ok) {
        const estimates = await checkResponse.json();
        if (estimates && estimates.length > 0) {
          toast.info('Apertura preventivo esistente...');
          navigate(`/estimates/${estimates[0].id}`);
          return;
        }
      }
      
      const lead = leads.find(l => l.id === leadId);
      if (!lead) {
        toast.error('Lead non trovato');
        await loadData();
        return;
      }
      
      const technicianId = lead.assigned_technician_id || user?.id;
      
      if (!technicianId) {
        toast.error('Nessun tecnico assegnato. Assegna prima un tecnico al lead.');
        await loadData();
        return;
      }
      
      const createResponse = await fetch(
        `/api/estimates`,
        {
          method: 'POST',
          headers: { withCredentials: true, 'Content-Type': 'application/json' },
          body: JSON.stringify({ lead_id: leadId, technician_id: technicianId, items: [] })
        }
      );
      
      if (createResponse.ok) {
        const newEstimate = await createResponse.json();
        toast.success('Preventivo creato! Apro il preventivatore...');
        navigate(`/estimates/${newEstimate.id}`);
      } else {
        const error = await createResponse.json();
        toast.error(error.detail || 'Errore nella creazione del preventivo');
        await loadData();
      }
    } catch (error) {
      console.error('Error creating estimate:', error);
      toast.error('Errore nell\'apertura del preventivo');
      await loadData();
    }
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      (lead.first_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.property_address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (lead.phone || '').includes(searchTerm);
    
    let matchesStatus = true;
    if (statusFilter === 'all') {
      matchesStatus = true;
    } else if (statusFilter === 'attivi') {
      // Filtro "Preventivi in lavorazione": solo lead non chiusi con almeno un preventivo in lavorazione
      matchesStatus = !['perso', 'convertito'].includes(lead.status) && 
                      !lead.is_deleted &&
                      estimatesInProgressMap[lead.id];
    } else if (statusFilter === 'convertito') {
      matchesStatus = lead.status === 'convertito';
    } else if (statusFilter === 'perso') {
      matchesStatus = lead.status === 'perso';
    } else {
      matchesStatus = lead.status === statusFilter;
    }
    return matchesSearch && matchesStatus;
  });

  // Group leads by status for Kanban
  const leadsByStatus = workflowSteps.reduce((acc, step) => {
    acc[step.status] = filteredLeads.filter(l => l.status === step.status);
    return acc;
  }, {});
  leadsByStatus['non_risponde'] = filteredLeads.filter(l => l.status === 'non_risponde');
  leadsByStatus['perso'] = filteredLeads.filter(l => l.status === 'perso');

  // Stats - "attivi" ora conta solo lead con preventivi in lavorazione
  const stats = {
    total: leads.length,
    attivi: leads.filter(l => 
      !['perso', 'convertito'].includes(l.status) && 
      !l.is_deleted &&
      estimatesInProgressMap[l.id]
    ).length,
    convertiti: leads.filter(l => l.status === 'convertito').length,
    persi: leads.filter(l => l.status === 'perso').length,
  };

  const getTechnicianName = (techId) => {
    const tech = technicians.find(t => t.id === techId);
    return tech ? `${tech.first_name} ${tech.last_name}` : 'Non assegnato';
  };

  // Funzione per ottenere il nome di qualsiasi utente assegnato (lavori o arredi)
  const getAssigneeName = (userId) => {
    if (!userId) return 'Non assegnato';
    // Cerca prima tra i tecnici
    const tech = technicians.find(t => t.id === userId);
    if (tech) return `${tech.first_name} ${tech.last_name}`;
    // Cerca tra tutti gli utenti
    const user = allUsers.find(u => u.id === userId);
    if (user) return `${user.first_name} ${user.last_name}`;
    return 'Utente sconosciuto';
  };

  // Lead Card wrapper with props - ora usa DraggableLeadCard se drag enabled
  const LeadCardWrapper = ({ lead, compact, isDragEnabled: cardDragEnabled }) => {
    // Se drag è abilitato globalmente E la card può essere draggata
    const shouldUseDraggable = isDragEnabled && viewMode === 'kanban';
    
    if (shouldUseDraggable) {
      return (
        <DraggableLeadCard
          lead={lead}
          compact={compact}
          isDragDisabled={false}
          estimatesCount={estimatesCountMap[lead.id] || 0}
          onShowDetail={handleNavigateToLead}
          onOpenEstimate={openOrCreateEstimate}
          onTransition={setShowTransitionModal}
          onAssign={() => setShowAssignModal(true)}
          onMove={handleMove}
          isMoving={movingLeadId === lead.id}
          getTechnicianName={getTechnicianName}
          getAssigneeName={getAssigneeName}
        />
      );
    }
    
    return (
      <LeadCard
        lead={lead}
        compact={compact}
        estimatesCount={estimatesCountMap[lead.id] || 0}
        onShowDetail={handleNavigateToLead}
        onOpenEstimate={openOrCreateEstimate}
        onTransition={setShowTransitionModal}
        onAssign={() => setShowAssignModal(true)}
        onMove={handleMove}
        isMoving={movingLeadId === lead.id}
        getTechnicianName={getTechnicianName}
        getAssigneeName={getAssigneeName}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7819]" />
      </div>
    );
  }

  // Fase F1 · flag ON → UI dual-pipeline; flag OFF/null → UI legacy
  if (pipelineEnabled === true) {
    return <LeadPipelineView />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h2">Lead</h1>
          <p className="text-body-small mt-1">Pipeline commerciale</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'kanban' ? 'bg-white shadow text-[#3C3C3C]' : 'text-[#737373]'
              }`}
              data-testid="view-kanban-btn"
            >
              Kanban
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white shadow text-[#3C3C3C]' : 'text-[#737373]'
              }`}
              data-testid="view-list-btn"
            >
              Lista
            </button>
          </div>
          
          {(isAdmin || isProjectManager) && (
            <Button 
              className="btn-primary" 
              onClick={() => setShowNewLeadModal(true)}
              data-testid="new-lead-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Lead
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <LeadStatsCards 
        stats={stats} 
        statusFilter={statusFilter} 
        setStatusFilter={setStatusFilter} 
      />

      {/* Search & Filter */}
      <LeadSearchFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      {/* === PANNELLO LEAD PERSI (sostituisce kanban/lista quando filter=perso) === */}
      {statusFilter === 'perso' && (
        <LostLeadsPanel
          leads={filteredLeads}
          onRecoveryDone={loadData}
          canManage={isAdmin || isProjectManager}
        />
      )}

      {/* Kanban View with Drag & Drop */}
      {statusFilter !== 'perso' && viewMode === 'kanban' && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <LeadKanbanView 
            leadsByStatus={leadsByStatus} 
            LeadCardComponent={LeadCardWrapper}
            isDragEnabled={isDragEnabled}
          />
          
          {/* Drag Overlay - mostra card fantasma durante il drag */}
          <DragOverlay>
            {activeDragLead ? (
              <div className="opacity-80 rotate-3 shadow-2xl">
                <LeadCard
                  lead={activeDragLead}
                  compact
                  estimatesCount={estimatesCountMap[activeDragLead.id] || 0}
                  onShowDetail={() => {}}
                  onOpenEstimate={() => {}}
                  onTransition={() => {}}
                  onAssign={() => {}}
                  onMove={() => {}}
                  isMoving={false}
                  getTechnicianName={getTechnicianName}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* List View */}
      {statusFilter !== 'perso' && viewMode === 'list' && (
        <LeadListView 
          filteredLeads={filteredLeads} 
          LeadCardComponent={LeadCardWrapper}
        />
      )}

      {/* Lead Detail Modal */}
      <LeadDetailModal
        lead={showLeadDetail}
        onClose={() => { setShowLeadDetail(null); cancelEditingLead(); }}
        isEditing={isEditingLead}
        editData={editLeadData}
        setEditData={setEditLeadData}
        onStartEdit={startEditingLead}
        onCancelEdit={cancelEditingLead}
        onSaveEdit={saveLeadChanges}
        savingLead={savingLead}
        leadEstimates={leadEstimates}
        loadingEstimates={loadingEstimates}
        leadContract={leadContract}
        loadingContract={loadingContract}
        onOpenEstimate={openOrCreateEstimate}
        onAssign={() => setShowAssignModal(true)}
        onTransition={setShowTransitionModal}
        getTechnicianName={getTechnicianName}
        isAdmin={isAdmin}
        onDelete={() => setShowDeleteDialog(true)}
      />

      {/* Delete Lead Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Elimina Lead
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il lead <strong>{showLeadDetail?.first_name} {showLeadDetail?.last_name}</strong>?
              <br /><br />
              <span className="text-red-600 font-medium">
                Questa azione è irreversibile e cancellerà anche tutti i preventivi, appuntamenti e contratti associati.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingLead}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLead}
              disabled={deletingLead}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingLead ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                'Elimina Definitivamente'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Assign Technician Modal */}
      <AssignTechnicianModal
        open={showAssignModal}
        onOpenChange={setShowAssignModal}
        technicians={technicians}
        selectedTechnician={selectedTechnician}
        setSelectedTechnician={setSelectedTechnician}
        transitionNote={transitionNote}
        setTransitionNote={setTransitionNote}
        onAssign={handleTransition}
        transitioning={transitioning}
        leadId={showLeadDetail?.id}
      />

      {/* Transition Confirmation Modal */}
      <TransitionConfirmModal
        open={!!showTransitionModal}
        onOpenChange={setShowTransitionModal}
        transitionData={showTransitionModal}
        transitionNote={transitionNote}
        setTransitionNote={setTransitionNote}
        onConfirm={handleTransition}
        transitioning={transitioning}
      />

      {/* New Lead Modal */}
      <NewLeadModal
        open={showNewLeadModal}
        onOpenChange={handleCloseNewLeadModal}
        newLead={newLead}
        setNewLead={setNewLead}
        onSubmit={handleCreateLead}
        onClose={handleCloseNewLeadModal}
        isSubmitting={isCreatingLead}
      />

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Modifiche non salvate
            </AlertDialogTitle>
            <AlertDialogDescription>
              Hai inserito dei dati nel form che non sono stati salvati. Se esci ora, perderai tutte le modifiche.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowUnsavedDialog(false)}>
              Continua a modificare
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmDiscard}
              className="bg-red-500 hover:bg-red-600"
            >
              Esci senza salvare
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ==================== MOVE DIALOGS ==================== */}
      
      {/* Appointment Dialog */}
      <AppointmentDialog
        open={!!showAppointmentDialog}
        onOpenChange={(open) => !open && setShowAppointmentDialog(null)}
        lead={showAppointmentDialog}
        technicians={technicians}
        rooms={rooms}
        users={allUsers}
        googleConnected={googleConnected}
        onConfirm={handleAppointmentConfirm}
        isSubmitting={!!movingLeadId}
      />

      {/* Lost Reason Dialog */}
      <LostReasonDialog
        open={!!showLostDialog}
        onOpenChange={(open) => !open && setShowLostDialog(null)}
        lead={showLostDialog}
        onConfirm={handleLostConfirm}
        isSubmitting={!!movingLeadId}
      />

      {/* Convert Confirm Dialog */}
      <ConvertConfirmDialog
        open={!!showConvertDialog}
        onOpenChange={(open) => !open && setShowConvertDialog(null)}
        lead={showConvertDialog}
        hasEstimate={showConvertDialog?.hasEstimate || false}
        hasContract={!!leadContract}
        onConfirm={handleConvertConfirm}
        isSubmitting={!!movingLeadId}
      />
    </div>
  );
};

export default LeadsPage;
